import * as React from 'react';
import { Navigation } from './Navigation.js';
import { Id } from '../../utils/ids.js';
import '../../constants.js';
import { NavigationItem } from './NavigationItem.js';
import { NavigationSubMenu } from './NavigationSubMenu.js';
import { VaiBreakpoint } from '@vaisala/rockhopper-design-tokens';
import useWindowDimensions from '../../utils/hooks/useWindowDimensions.js';
import { parseHierarchy, parseRouteHierarchy, createSubMenuLookup, normalizeLocation } from './navigationUtils.js';

const createIsTopLevel = (subMenuLookup) => (key) => subMenuLookup[key] === key;
const SimpleNavigation = (props) => {
  const {
    onCollapseChange,
    onSelect,
    location,
    productName,
    className,
    animated,
    htmlId,
    hierarchy,
    root,
    dataTa,
    _collapseMode = "stripe",
    header,
    footer
  } = props;
  const [collapsed, setCollapsed] = React.useState(props.collapsed);
  const parsedHierarchy = React.useMemo(() => {
    if (Array.isArray(hierarchy)) {
      return parseHierarchy(hierarchy, root);
    }
    return parseRouteHierarchy({ hierarchy, root });
  }, [hierarchy, root]);
  const subMenuLookup = React.useMemo(() => createSubMenuLookup(parsedHierarchy), [parsedHierarchy]);
  const normalizedLocation = React.useMemo(() => normalizeLocation({ location, root }, subMenuLookup), [location, root, subMenuLookup]);
  const isTopLevel = React.useMemo(() => createIsTopLevel(subMenuLookup), [subMenuLookup]);
  const [openKeys, setOpenKeys] = React.useState(() => {
    if (normalizedLocation) {
      return [subMenuLookup[normalizedLocation]];
    }
    return [];
  });
  const dimensions = useWindowDimensions();
  const mobileBreakpoint = React.useMemo(() => Number(VaiBreakpoint.M.replace("px", "")), []);
  const id = Id.create(htmlId, ["path navigation"]);
  const handleItemSelect = React.useCallback(({ menuKey }) => {
    if (dimensions.width <= mobileBreakpoint) {
      setCollapsed(true);
    }
    if (menuKey === normalizedLocation) {
      return;
    }
    if (isTopLevel(menuKey)) {
      setOpenKeys([]);
    } else {
      setOpenKeys([subMenuLookup[menuKey]]);
    }
    onSelect?.(menuKey);
  }, [dimensions, mobileBreakpoint, normalizedLocation, subMenuLookup, setOpenKeys, isTopLevel, onSelect]);
  const onSubmenuToggle = React.useCallback(({ menuKey }) => {
    setOpenKeys((keys) => {
      const index = keys.indexOf(menuKey);
      const isOpening = index === -1;
      if (_collapseMode === "bar" || isTopLevel(normalizedLocation)) {
        return isOpening ? [menuKey] : [];
      }
      if (!isOpening) {
        return [...keys.slice(0, index), ...keys.slice(index + 1)];
      }
      if (keys.length < 2) {
        return keys.concat(menuKey);
      } else {
        return [subMenuLookup[normalizedLocation], menuKey];
      }
    });
  }, [setOpenKeys, isTopLevel, subMenuLookup, normalizedLocation, _collapseMode]);
  React.useEffect(() => {
    setOpenKeys(() => {
      if (normalizedLocation) {
        return [subMenuLookup[normalizedLocation]];
      }
      return [];
    });
  }, [normalizedLocation, subMenuLookup]);
  const parsedItems = React.useMemo(() => parsedHierarchy.map(({ key, title, items, item, icon }) => {
    if (item) {
      return /* @__PURE__ */ React.createElement(NavigationItem, {
        htmlId: id.copyWith([key]).toHtmlId(),
        key,
        menuKey: key,
        icon
      }, item);
    }
    return /* @__PURE__ */ React.createElement(NavigationSubMenu, {
      key,
      menuKey: key,
      title,
      htmlId: id.copyWith([key]).toHtmlId(),
      icon
    }, items.map((subMenuItem) => /* @__PURE__ */ React.createElement(NavigationItem, {
      key: subMenuItem.key,
      menuKey: subMenuItem.key,
      htmlId: id.copyWith([subMenuItem.key]).toHtmlId(),
      icon: subMenuItem.icon
    }, subMenuItem.title)));
  }), [parsedHierarchy, id]);
  const onCollapseToggle = React.useCallback(() => {
    setCollapsed((c) => {
      onCollapseChange?.(!c);
      if (_collapseMode === "bar" && !c) {
        setOpenKeys([]);
      }
      return !c;
    });
  }, [setCollapsed, _collapseMode, onCollapseChange]);
  return /* @__PURE__ */ React.createElement(Navigation, {
    htmlId,
    openSubmenuKeys: openKeys,
    collapsed,
    selectedKey: normalizedLocation,
    productName,
    onCollapseToggle,
    onSubmenuToggle,
    onSelectItem: handleItemSelect,
    className,
    animated,
    "data-ta": dataTa,
    _collapseMode,
    header,
    footer
  }, parsedItems);
};

export { SimpleNavigation };
